import React, { useState } from "react";
import {
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  ThemeProvider,
} from "@mui/material";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";

const trussTypes = [
  { name: "Common", img: "/images/common.png" },
  { name: "Attic", img: "/images/attic.png" },
  { name: "Scissor", img: "/images/scissor.png" },
  { name: "Gambrel Attic", img: "/images/gambrel.png" },
];

const pitches = ["4/12", "5/12", "6/12"];
const spans = Array.from({ length: 15 }, (_, i) => 12 + i * 2);
const raisedHeelOptions = Array.from({ length: 13 }, (_, i) => 6 + i);
const centers = ["2'", "4'"];

const heelHeights = {
  "4/12": '3-15/16"',
  "5/12": '4-1/16"',
  "6/12": '4-3/16"',
};

const StockTrussForm = () => {
  // Every input on this page is a MUI control, and MUI's palette can't read
  // Tailwind's `dark:` classes — without this the labels and selected values
  // stay near-black on the dark cards.
  const muiTheme = useAppMuiTheme();
  const [selectedType, setSelectedType] = useState("Common");
  const [center, setCenter] = useState("2'");
  const [pitch, setPitch] = useState("4/12");
  const [span, setSpan] = useState(12);
  const [zip, setZip] = useState("");
  const [qty, setQty] = useState(1);
  const [matchGables, setMatchGables] = useState(false);
  const [gablesQty, setGablesQty] = useState(0);
  const [heelType, setHeelType] = useState("Standard");
  const [raisedHeelHeight, setRaisedHeelHeight] = useState(6);
  const [trusses, setTrusses] = useState([]);

  const addToQuote = () => {
    const newTruss = {
      type: selectedType,
      center,
      pitch,
      span,
      zip,
      qty,
      matchGables,
      gablesQty,
      heelType,
      heelHeight:
        heelType === "Standard" ? heelHeights[pitch] : `${raisedHeelHeight}\"`,
    };
    setTrusses([...trusses, newTruss]);
  };

  return (
    <ThemeProvider theme={muiTheme}>
    <div className="w-full max-w-5xl space-y-6">
      {/* Section 1 */}
      <div>
        <h2 className="mb-4 text-lg font-semibold">
          <span className="app-muted">1.</span> Select Truss Type
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {trussTypes.map((truss) => (
            <Card
              key={truss.name}
              elevation={0}
              className={`app-card cursor-pointer rounded-2xl border transition hover:-translate-y-0.5 ${
                selectedType === truss.name ? "ring-2 ring-sky-500" : ""
              }`}
              onClick={() => setSelectedType(truss.name)}
            >
              <CardContent className="flex flex-col items-center">
                {/* Line drawings — kept on a light tile so they read in dark mode. */}
                <span className="flex h-24 w-full items-center justify-center rounded-xl bg-white p-2">
                  <img
                    src={truss.img}
                    alt={truss.name}
                    className="h-full object-contain"
                  />
                </span>
                <p className="mt-2 font-medium">{truss.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Section 2 */}
      <div className="app-card rounded-2xl border p-5 sm:p-6">
        <h2 className="mb-4 text-lg font-semibold">
          <span className="app-muted">2.</span> Specifications
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormControl fullWidth>
            <InputLabel>Spacing</InputLabel>
            <Select
              value={center}
              label="Spacing"
              onChange={(e) => setCenter(e.target.value)}
            >
              {centers.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Pitch</InputLabel>
            <Select
              value={pitch}
              label="Pitch"
              onChange={(e) => setPitch(e.target.value)}
            >
              {pitches.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Span</InputLabel>
            <Select
              value={span}
              label="Span"
              onChange={(e) => setSpan(e.target.value)}
            >
              {spans.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}'
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Zip Code / Address"
            fullWidth
            value={zip}
            onChange={(e) => setZip(e.target.value)}
          />

          <TextField
            type="number"
            label="Quantity"
            fullWidth
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />

          <FormControl fullWidth>
            <InputLabel>Gables Match?</InputLabel>
            <Select
              value={matchGables ? "Yes" : "No"}
              label="Gables Match?"
              onChange={(e) => setMatchGables(e.target.value === "Yes")}
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </Select>
          </FormControl>

          {matchGables && (
            <TextField
              type="number"
              label="Gable Qty"
              fullWidth
              value={gablesQty}
              onChange={(e) => setGablesQty(e.target.value)}
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Heel Type</InputLabel>
            <Select
              value={heelType}
              label="Heel Type"
              onChange={(e) => setHeelType(e.target.value)}
            >
              <MenuItem value="Standard">
                Standard ({heelHeights[pitch]})
              </MenuItem>
              <MenuItem value="Raised">Raised</MenuItem>
            </Select>
          </FormControl>

          {heelType === "Raised" && (
            <FormControl fullWidth>
              <InputLabel>Raised Heel Height (inches)</InputLabel>
              <Select
                value={raisedHeelHeight}
                label="Raised Heel Height"
                onChange={(e) => setRaisedHeelHeight(e.target.value)}
              >
                {raisedHeelOptions.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}"
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      {/* Section 3 */}
      <div className="app-card rounded-2xl border p-5 sm:p-6">
        <h2 className="mb-4 text-lg font-semibold">
          <span className="app-muted">3.</span> Quote Summary
        </h2>
        <Button variant="contained" onClick={addToQuote}>
          Add to Quote
        </Button>

        <div className="mt-6">
          {trusses.length > 0 && (
            <div className="app-surface overflow-hidden rounded-xl border">
              <div className="overflow-x-auto">
              <table className="app-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Center</th>
                    <th>Pitch</th>
                    <th>Span</th>
                    <th>Qty</th>
                    <th>Gables?</th>
                    <th>Gable Qty</th>
                    <th>Heel</th>
                    <th>Zip</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {trusses.map((t, i) => (
                    <tr key={i}>
                      <td>{t.type}</td>
                      <td>{t.center}</td>
                      <td>{t.pitch}</td>
                      <td>{t.span}'</td>
                      <td>{t.qty}</td>
                      <td>{t.matchGables ? "Yes" : "No"}</td>
                      <td>{t.matchGables ? t.gablesQty : "-"}</td>
                      <td>
                        {t.heelType} ({t.heelHeight})
                      </td>
                      <td>{t.zip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </ThemeProvider>
  );
};

export default StockTrussForm;
